import { convertToDecimals, fallbackText, IAdminTransactions, TimeFormat, TransactionStatus, TransactionTypes, TypeOfUser } from '@pharmaplan/common';
import strings from '../../../localization';
import { IUserProfileLinkSignature } from '../../../hooks/Admin/useUserProfileLink';
import { getDayMonthDateYear, getHourMinute } from '../../Reports/helpers';
import { DynamicTableCell, nameLabels } from '../../../helpers/Constants';
import generateInvoice from '../../../assets/svg/generateInvoice.svg';

import styles from './styles';

const { customLink, text, chip } = DynamicTableCell;
const { Unlimited, Basic, Refund } = TransactionTypes;
const { Success, Failure, Progress, Refund: RefundStatus } = TransactionStatus;

const TransactionStatusStylesMap = {
  [Basic]: styles.basic,
  [Unlimited]: styles.unlimited,
  [Refund]: styles.refund,
};

export const transactionStatusLabelMap = () =>
  ({
    [Success]: strings.success,
    [Failure]: strings.failure,
    [Progress]: strings.paymentInProgress,
    [RefundStatus]: strings.refundInitiated,
  });

export const TransactionStatusStringMap = () =>
  ({
    [Basic]: strings.standard,
    [Unlimited]: strings.unlimited,
    [Refund]: strings.refund,
  });

export const transactionHeaders = () =>
  [
    {
      key: '0',
      label: strings.transactionId,
      sortingKey: 'transactionId',
    },
    {
      key: '1',
      label: strings.formatString(strings.dateTimeSuffix, strings.transactions) as string,
      sortingKey: 'transactionDate',
    },
    {
      key: '2',
      label: strings.pharmacy,
      sortingKey: 'pharmacyName',
    },
    {
      key: '3',
      label: nameLabels().roleLabel,
      sortingKey: 'professionalName',
    },
    {
      key: '4',
      label: strings.transactionType,
      sortingKey: 'transactionType',
    },
    {
      key: '5',
      label: strings.amount,
      sortingKey: 'amount',
    },
    {
      key: '6',
      label: strings.status,
      sortingKey: 'status',
    },
    {
      key: '7',
      label: strings.action,
    },
  ];

export const transactionRowConfig = (
  list: Array<IAdminTransactions>,
  downloadPaymentReport: (paymentId: string) => () => void,
  goToLink?: (codeId: string) => () => void,
  goToUser?: IUserProfileLinkSignature,
) =>
  list?.map((item) => {
    const {
      transactionId,
      transactionDate,
      transactionType,
      professionalId,
      professionalName,
      pharmacyName,
      status,
      pharmacyId,
      amount,
      paymentId,
    } = item ?? {};

    const date = `${getDayMonthDateYear(transactionDate)} | ${getHourMinute(transactionDate, TimeFormat.twelveHours)}`;

    return {
      key: transactionId,
      data: [
        {
          key: `0-${transactionId}`,
          minWidth: 200,
          value: {
            label: transactionId,
            handlePress: goToLink?.(transactionId),
          },
          type: customLink,
        },
        {
          key: `1-${transactionId}`,
          value: date,
          type: text,
        },
        {
          key: `2-${transactionId}`,
          minWidth: 200,
          value: {
            label: pharmacyName,
            handlePress: goToUser?.({
              personnelId: pharmacyId,
              userType: TypeOfUser.pharmacy,
            }),
          },
          type: customLink,
        },
        {
          key: `3-${transactionId}`,
          value: professionalName ? {
            label: professionalName,
            handlePress: goToUser?.({
              personnelId: professionalId ?? '',
              userType: TypeOfUser.pharmacist,
            }),
          } : fallbackText,
          type: professionalName ? customLink : text,
        },

        {
          key: `4-${transactionId}`,
          label: transactionType,
          value: {
            label: TransactionStatusStringMap()[transactionType as keyof typeof TransactionStatusStringMap],
            style: TransactionStatusStylesMap[transactionType as keyof typeof TransactionStatusStylesMap],
          },
          type: chip,
        },
        {
          key: `5-${transactionId}`,
          value: `$${convertToDecimals(amount)}`,
          type: text,
        },
        {
          key: `6-${transactionId}`,
          value: transactionStatusLabelMap()[status as keyof typeof transactionStatusLabelMap],
          type: text,
        },
        {
          key: '6',
          value: [
            {
              key: '1',
              tooltip: strings.generateInvoice,
              icon: generateInvoice,
              onClick: downloadPaymentReport(paymentId ?? ''),
            },
          ],
          type: DynamicTableCell.bar,
        },
      ],
    };
  });
